"use client";
// ===================================================
// GAM Command Center — מחברת (Pinkas) — unified workspace v4
// One component for the vClick panel and the full page. Top bar → tray →
// list (right, RTL-first) + active note. Wide containers show both side by
// side; narrow ones fold the list into a drawer opened from the top bar.
// Workspace state (tray, active note, list view, positions) is kept per user
// and tenant in this browser only.
// Spec: docs/specs/pinkas-unified-workspace-20260924.md
// ===================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { ExternalLink, List, Maximize2, Minimize2, Plus, Search, X } from "lucide-react";
import { Button, ConfirmDialog, EmptyState, Input } from "@/components/ui";
import { useAuth } from "@/contexts/AuthContext";
import {
  useAddPinkas,
  useDeletePinkas,
  usePinkas,
  useUpdatePinkas,
  type PinkasItem,
  type PinkasPatch,
  type PinkasPriority,
  type PinkasStatus,
} from "@/lib/cc-pinkas-queries";
import type { SaveNoteFn } from "@/lib/pinkas/useNoteAutosave";
import {
  DEFAULT_WORKSPACE,
  UNTITLED,
  draftKey as makeDraftKey,
  loadWorkspace,
  matchesSearch,
  prunePersisted,
  saveWorkspace,
  trayAdd,
  trayClose,
  trayMove,
  workspaceKey,
  type NotePosition,
  type WorkspaceState,
} from "@/lib/pinkas/workspace";
import { parseQuickAdd } from "@/lib/pinkas/vocab";
import { IconAction } from "./PinkasControls";
import { PinkasNoteEditor } from "./PinkasNoteEditor";
import { PinkasNoteList, type QuickAddInput } from "./PinkasNoteList";
import { PinkasTray } from "./PinkasTray";

const TENANT = "gam";
/** Below this container width the list becomes a drawer. */
const WIDE_MIN_PX = 720;
const SEEN_KEY = "cc_pinkas_seen";

const PRIORITY_RANK: Record<PinkasPriority, number> = { high: 0, normal: 1, low: 2 };
const STATUS_RANK: Record<PinkasStatus, number> = { doing: 0, open: 1, done: 2 };
const byUpdated = (a: PinkasItem, b: PinkasItem) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();

export interface PinkasWorkspaceProps {
  variant?: "panel" | "page";
  /** Panel only: close the panel. Leaving unmounts the note, which saves it. */
  onClose?: () => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
  /** Panel only: called when the user follows the full-page link. */
  onOpenFullPage?: () => void;
}

export function PinkasWorkspace({ variant = "page", onClose, expanded, onToggleExpand, onOpenFullPage }: PinkasWorkspaceProps) {
  const { user } = useAuth();
  const userId = user?.id ?? "anon";
  const wsKey = workspaceKey(TENANT, userId);

  const { data: items = [], isLoading, isSuccess } = usePinkas();
  const add = useAddPinkas();
  const update = useUpdatePinkas();
  const del = useDeletePinkas();

  // ---- workspace state (restored after mount — localStorage breaks SSR) --
  const [ws, setWs] = useState<WorkspaceState>(DEFAULT_WORKSPACE);
  const [loaded, setLoaded] = useState(false);
  const pruned = useRef(false);
  useEffect(() => {
    setWs(loadWorkspace(wsKey));
    setLoaded(true);
    pruned.current = false;
  }, [wsKey]);
  useEffect(() => {
    if (loaded) saveWorkspace(wsKey, ws);
  }, [loaded, wsKey, ws]);
  const patchWs = useCallback((p: Partial<WorkspaceState> | ((s: WorkspaceState) => Partial<WorkspaceState>)) => {
    setWs((s) => ({ ...s, ...(typeof p === "function" ? p(s) : p) }));
  }, []);

  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  // A note that was deleted or is no longer visible to this user drops out
  // of the restored state quietly — once the first real list has arrived.
  useEffect(() => {
    if (!loaded || !isSuccess || pruned.current) return;
    pruned.current = true;
    setWs((s) => prunePersisted(s, new Set(itemsById.keys())));
  }, [loaded, isSuccess, itemsById]);
  // …and later removals (deleted by Claude, by another tab) the same way.
  useEffect(() => {
    if (!pruned.current) return;
    setWs((s) => {
      const gone = s.tray.some((id) => !itemsById.has(id)) || (s.activeId && !itemsById.has(s.activeId));
      return gone ? prunePersisted(s, new Set(itemsById.keys())) : s;
    });
  }, [itemsById]);

  // ---- "new from Claude since you last looked" ---------------------------
  const [lastSeen, setLastSeen] = useState<number | null>(null);
  useEffect(() => {
    try {
      const v = window.localStorage.getItem(SEEN_KEY);
      setLastSeen(v ? Number(v) : 0);
      window.localStorage.setItem(SEEN_KEY, String(Date.now()));
    } catch {
      setLastSeen(0);
    }
  }, []);
  const isNew = useCallback(
    (it: PinkasItem) =>
      lastSeen !== null && it.created_by === "claude" && new Date(it.updated_at || it.created_at).getTime() > lastSeen,
    [lastSeen],
  );

  // ---- layout: wide vs narrow, by the container, not the window ----------
  const root = useRef<HTMLDivElement>(null);
  const [wide, setWide] = useState(true);
  useEffect(() => {
    const el = root.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setWide(entry.contentRect.width >= WIDE_MIN_PX));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const listVisible = wide ? ws.listOpen : drawerOpen;
  const toggleList = () => (wide ? patchWs((s) => ({ listOpen: !s.listOpen })) : setDrawerOpen((o) => !o));

  // ---- the list ------------------------------------------------------------
  const visible = useMemo(() => {
    const rows = items.filter((it) => {
      if (ws.view === "archived" ? !it.archived : it.archived) return false;
      if (ws.view === "pinned" && !it.pinned) return false;
      if (ws.filter === "gal" && it.assignee === "claude") return false;
      if (ws.filter === "claude" && it.assignee === "gal") return false;
      return matchesSearch(it, ws.search);
    });
    return rows.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      if (ws.sort === "priority")
        return PRIORITY_RANK[a.priority ?? "normal"] - PRIORITY_RANK[b.priority ?? "normal"] || byUpdated(a, b);
      if (ws.sort === "status") return STATUS_RANK[a.status] - STATUS_RANK[b.status] || byUpdated(a, b);
      return byUpdated(a, b);
    });
  }, [items, ws.view, ws.filter, ws.sort, ws.search]);
  const claudeQueue = items.filter((i) => !i.archived && i.assignee === "claude" && i.status !== "done").length;
  const newCount = items.filter(isNew).length;
  const traySet = useMemo(() => new Set(ws.tray), [ws.tray]);

  // ---- actions ----------------------------------------------------------------
  const [actionError, setActionError] = useState("");
  const open = useCallback(
    (id: string) => {
      patchWs({ activeId: id }); // opening never adds to the tray
      if (!wide) setDrawerOpen(false);
    },
    [patchWs, wide],
  );
  const toggleTray = useCallback(
    (id: string) => patchWs((s) => ({ tray: s.tray.includes(id) ? s.tray.filter((x) => x !== id) : trayAdd(s.tray, id) })),
    [patchWs],
  );
  const closeTab = useCallback((id: string) => patchWs((s) => trayClose(s.tray, id, s.activeId)), [patchWs]);
  const moveTab = useCallback((from: string, to: string) => patchWs((s) => ({ tray: trayMove(s.tray, from, to) })), [patchWs]);

  const patchNote = useCallback(
    (id: string, patch: PinkasPatch) => {
      setActionError("");
      update.mutate(
        { id, patch },
        { onError: (e) => setActionError(`העדכון נכשל: ${e instanceof Error ? e.message : ""}`) },
      );
    },
    [update],
  );
  const saveNote: SaveNoteFn = useCallback((id, patch) => update.mutateAsync({ id, patch }), [update]);

  async function quickAdd({ raw, kind, assignee }: QuickAddInput) {
    const parsed = parseQuickAdd(raw);
    if (!parsed.text) throw new Error("אין טקסט לשמירה");
    await add.mutateAsync({
      kind: parsed.kind ?? kind,
      text: parsed.text,
      assignee: parsed.assignee ?? assignee,
      priority: parsed.priority ?? null,
      tags: parsed.tags,
    });
  }

  async function createNote() {
    setActionError("");
    try {
      const row = await add.mutateAsync({ text: UNTITLED, kind: "note" });
      if (ws.view === "archived") patchWs({ view: "active" });
      open(row.id);
    } catch (e) {
      setActionError(`יצירת הפתק נכשלה: ${e instanceof Error ? e.message : ""}`);
    }
  }

  const [toDelete, setToDelete] = useState<PinkasItem | null>(null);
  async function confirmDelete() {
    const it = toDelete;
    if (!it) return;
    await del.mutateAsync(it.id);
    patchWs((s) => {
      const next = s.tray.includes(it.id) ? trayClose(s.tray, it.id, s.activeId) : { tray: s.tray, activeId: s.activeId };
      return { tray: next.tray, activeId: s.activeId === it.id ? null : next.activeId };
    });
    setToDelete(null);
  }

  const setPosition = useCallback(
    (id: string, pos: NotePosition) => patchWs((s) => ({ positions: { ...s.positions, [id]: pos } })),
    [patchWs],
  );

  const active = ws.activeId ? itemsById.get(ws.activeId) ?? null : null;

  // Escape closes the narrow drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setDrawerOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const list = (
    <PinkasNoteList
      items={visible}
      loading={isLoading || !loaded}
      activeId={ws.activeId}
      tray={traySet}
      view={ws.view}
      onView={(view) => patchWs({ view })}
      filter={ws.filter}
      onFilter={(filter) => patchWs({ filter })}
      sort={ws.sort}
      onSort={(sort) => patchWs({ sort })}
      searching={!!ws.search.trim()}
      claudeQueue={claudeQueue}
      newCount={newCount}
      isNew={isNew}
      onOpen={open}
      onToggleTray={toggleTray}
      onPatch={patchNote}
      onDelete={setToDelete}
      onQuickAdd={quickAdd}
      scrollTop={ws.listScrollTop}
      onScrollTop={(listScrollTop) => patchWs({ listScrollTop })}
    />
  );

  return (
    <div
      ref={root}
      dir="rtl"
      className="flex h-full min-h-0 flex-col bg-[var(--theme-bg)] text-[var(--theme-text)]"
      data-variant={variant}
      data-layout={wide ? "wide" : "narrow"}
    >
      {/* top bar ------------------------------------------------------------ */}
      <header className="flex items-center gap-1.5 border-b border-[var(--border-hairline)] px-2 py-1.5">
        <IconAction icon={List} label={listVisible ? "הסתר רשימה" : "הצג רשימה"} active={listVisible} onClick={toggleList} />
        <h2 className="shrink-0 px-1 text-sm font-semibold">מחברת</h2>
        <Button
          type="button"
          size="sm"
          icon={Plus}
          aria-label="פתק חדש"
          onClick={() => void createNote()}
          loading={add.isPending}
          style={{ height: 28, paddingInline: wide ? 10 : 7, borderRadius: 6, flexShrink: 0 }}
        >
          {wide ? "פתק חדש" : null}
        </Button>
        <div className="min-w-0 max-w-xs flex-1">
          <Input
            inputSize="sm"
            type="search"
            aria-label="חיפוש בכותרת ובתוכן"
            placeholder="חיפוש בכותרת ובתוכן…"
            iconStart={<Search size={14} aria-hidden />}
            value={ws.search}
            onChange={(e) => {
              patchWs({ search: e.target.value });
              if (!wide && e.target.value) setDrawerOpen(true);
            }}
          />
        </div>
        {wide ? <span className="flex-1" /> : null}
        {variant === "panel" ? (
          <Link
            href="/dashboard/pinkas"
            onClick={onOpenFullPage}
            aria-label="עמוד מלא"
            title="עמוד מלא"
            className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11px] text-[var(--theme-text-muted)] hover:bg-[var(--role-hover-bg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--role-focus-ring)]"
          >
            <ExternalLink size={12} aria-hidden />
            {wide ? "עמוד מלא" : null}
          </Link>
        ) : null}
        {onToggleExpand ? (
          <IconAction icon={expanded ? Minimize2 : Maximize2} label={expanded ? "חזרה לגודל רגיל" : "הרחב לתצוגה מלאה"} onClick={onToggleExpand} />
        ) : null}
        {onClose ? <IconAction icon={X} label="סגור מחברת" onClick={onClose} /> : null}
      </header>

      {/* tray ------------------------------------------------------------------ */}
      <div className="border-b border-[var(--border-hairline)] bg-[var(--theme-surface)]">
        <PinkasTray tray={ws.tray} itemsById={itemsById} activeId={ws.activeId} onSelect={open} onClose={closeTab} onMove={moveTab} />
      </div>

      {actionError ? (
        <div role="alert" className="flex items-center gap-2 border-b border-[var(--border-hairline)] px-3 py-1.5 text-xs text-[var(--role-error)]">
          <span className="flex-1">{actionError}</span>
          <IconAction icon={X} label="סגור הודעה" onClick={() => setActionError("")} />
        </div>
      ) : null}

      {/* body: list (right in RTL) + note --------------------------------------- */}
      <div className="relative flex min-h-0 flex-1">
        {wide && ws.listOpen ? (
          <aside aria-label="רשימת המחברת" className="flex w-[300px] shrink-0 flex-col border-e border-[var(--border-hairline)] bg-[var(--theme-surface)]">
            {list}
          </aside>
        ) : null}
        {!wide && drawerOpen ? (
          <>
            <div aria-hidden className="absolute inset-0 z-10 bg-[var(--theme-bg)] opacity-60" onClick={() => setDrawerOpen(false)} />
            <aside
              aria-label="רשימת המחברת"
              className="absolute inset-y-0 start-0 z-20 flex w-[min(320px,88%)] flex-col border-e border-[var(--theme-border)] bg-[var(--theme-surface)] shadow-xl"
            >
              {list}
            </aside>
          </>
        ) : null}

        <main className="min-w-0 flex-1">
          {active ? (
            <PinkasNoteEditor
              key={active.id}
              note={active}
              draftKey={makeDraftKey(TENANT, userId, active.id)}
              save={saveNote}
              onPatch={(p) => patchNote(active.id, p)}
              inTray={traySet.has(active.id)}
              onToggleTray={() => toggleTray(active.id)}
              detailsOpen={ws.detailsOpen}
              onToggleDetails={() => patchWs((s) => ({ detailsOpen: !s.detailsOpen }))}
              position={ws.positions[active.id]}
              onPosition={(pos) => setPosition(active.id, pos)}
              compact={!wide}
            />
          ) : (
            <div className="flex h-full items-center justify-center p-6">
              <EmptyState title="אין פתק פתוח">
                <p className="mb-3">בחר פתק מהרשימה, או פתח פתק חדש.</p>
                <div className="flex justify-center gap-2">
                  {!listVisible ? (
                    <Button type="button" size="sm" variant="secondary" icon={List} onClick={toggleList}>
                      הצג רשימה
                    </Button>
                  ) : null}
                  <Button type="button" size="sm" icon={Plus} onClick={() => void createNote()} loading={add.isPending}>
                    פתק חדש
                  </Button>
                </div>
              </EmptyState>
            </div>
          )}
        </main>
      </div>

      <ConfirmDialog
        open={!!toDelete}
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="למחוק את הפתק?"
        subject={toDelete?.text}
        description="המחיקה סופית. לסגירת לשונית במגש אין צורך למחוק — הפתק נשאר."
        confirmLabel="מחק"
        tone="danger"
      />
    </div>
  );
}
